import * as React from 'react'
import { useNavigate } from 'react-router-dom'

import { Features } from 'generated/featureDefinitions'

import { observer } from 'mobx-react'

import useAsyncAction from 'modules/core/hooks/useAsyncAction'
import { useAsyncData } from 'modules/core/hooks/useAsyncData'
import { useFeatureAvailability } from 'modules/core/hooks/useFeatureAvailability'
import { useMemo } from 'modules/core/hooks/useMemo'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import i18n from 'modules/core/lib/i18n'

import { ResponsivePage } from 'modules/layout/components/ResponsivePage'
import LeaveTypeFormModel from 'modules/timeoff/lib/LeaveTypeForm'
import leaveTypes from 'modules/timeoff/store/collections/leaveTypes'
import LeaveType, { COLOR_CODES } from 'modules/timeoff/store/models/LeaveType'

import AllowanceSection from './AllowanceSection'
import ApprovalSection from './ApprovalSection'
import BasicInformationSection from './BasicInformationSection'
import DurationSection from './DurationSection'
import PermissionsSection from './PermissionsSection'
import SkeletonLoader from './SkeletonLoader'

interface Props {
  leaveTypeName?: string
  leaveTypeId?: string
}

const UpsertLeaveType = ({ leaveTypeName, leaveTypeId }: Props) => {
  const { navigation } = useNavigation()
  const navigate = useNavigate()
  const [leaveType, setLeaveType] = React.useState<LeaveType | null>(null)

  const leaveTypeForm = useMemo(
    () =>
      new LeaveTypeFormModel({
        name: leaveTypeName || leaveType?.name,
        description: '',
        color: COLOR_CODES[0],
        accrues: false,
        attachment: false,
        visibility: false,
        workable: false,
        payable: false,
        approval_required: true,
        is_attachment_mandatory: false,
        half_days_units_enabled: true,
        max_days_in_cents: null,
        min_days_in_cents: null,
        allow_endless: false,
        restricted: false,
        details_required: false,
        ...(leaveType?.attributes || {}),
      }),
    [leaveTypeName, leaveType?.attributes.color]
  )

  const {
    loading: timeoffRestrictedLeaveTypesLoading,
    featureAvailability: timeoffRestrictedLeaveTypesAvailability,
  } = useFeatureAvailability(Features.timeoff_restricted_leave_types)
  const timeoffRestrictedLeaveTypesEnabled = timeoffRestrictedLeaveTypesAvailability.available

  const { loading: loadingLeaveTypes } = useAsyncData(async () => {
    if (!leaveTypeId) return

    await leaveTypes.fetch()

    const model = leaveTypeId
      ? leaveTypes.find({
          id: leaveTypeId,
        })
      : null

    if (!model) return

    setLeaveType(model)
    leaveTypeForm.setValues(model.toJS())
  }, [leaveTypeId])

  const loading = loadingLeaveTypes || timeoffRestrictedLeaveTypesLoading

  const { running: submitting, performAction: onSubmit } = useAsyncAction(async () => {
    leaveType ? await leaveTypeForm.save(leaveType) : await leaveTypeForm.create(leaveTypes)

    navigate(navigation.settings.timeoffPolicies.absenceTypes.fullPath())
  })

  if (loading) return <SkeletonLoader />

  const canEditDetails = (() => {
    if (!leaveType) return true

    return leaveType.isNonPayrollMedical ? true : leaveType.canEditDetails
  })()

  const actionLabel = i18n.t('settings.leave_types.page.save_button')

  const backToPoliciesPage = () => {
    navigate(navigation.settings.timeoffPolicies.absenceTypes.fullPath())
  }

  return (
    <ResponsivePage.Body
      loading={loading}
      error={false}
      showHeader={false}
      transactionalActions={{
        primary: {
          label: actionLabel,
          onClick: onSubmit,
          disabled: submitting, //|| !leaveTypeForm.isLeaveTypeFormComplete,
        },
        secondary: {
          label: i18n.t('common.cancel'),
          onClick: backToPoliciesPage,
        },
      }}
    >
      <BasicInformationSection
        leaveType={leaveType}
        leaveTypeForm={leaveTypeForm}
        canEditDetails={canEditDetails}
      />
      <AllowanceSection leaveTypeForm={leaveTypeForm} canEditDetails={canEditDetails} />
      <DurationSection leaveTypeForm={leaveTypeForm} canEditDetails={canEditDetails} />
      <PermissionsSection
        leaveTypeForm={leaveTypeForm}
        canEditDetails={canEditDetails}
        timeoffRestrictedLeaveTypesEnabled={timeoffRestrictedLeaveTypesEnabled}
      />
      <ApprovalSection leaveTypeForm={leaveTypeForm} canEditDetails={canEditDetails} />
    </ResponsivePage.Body>
  )
}

export default observer(UpsertLeaveType)
